import { type ClientConfig, type ClientPerspective, createClient, type SanityClient } from "@sanity/client";
import { defineQuery } from "groq";
import posthog from "posthog-js";
import { Sentry } from "~/lib/sentry";
import type { SongTypeDevotional } from "~/lib/songTypes";
import type { GetEpisodeBySlugAndSeriesSlugQueryResult } from "~/sanity/sanity.types";

const perspective = (import.meta.env.VITE_SANITY_STUDIO_PERSPECTIVE as ClientPerspective | undefined) ?? "published";
const token = import.meta.env.VITE_SANITY_STUDIO_DRAFT_READ_KEY;
const cachedApiHost = import.meta.env.VITE_SANITY_CACHE_API_DOMAIN || "https://api.sanity.io";
const cachedUseProjectHostname = import.meta.env.VITE_SANITY_USE_PROJECT_HOSTNAME !== "false";
const cachedRequestTimeoutMs = Number(import.meta.env.VITE_SANITY_CACHE_TIMEOUT_MS ?? 8000);
const configuredFallbackTelemetrySampleRate = Number(
  import.meta.env.VITE_SANITY_CACHE_FALLBACK_TELEMETRY_SAMPLE_RATE ?? 0.01,
);
const fallbackTelemetrySampleRate = Number.isFinite(configuredFallbackTelemetrySampleRate)
  ? Math.min(1, Math.max(0, configuredFallbackTelemetrySampleRate))
  : 0.01;
const configuredFallbackTelemetrySessionEventLimit = Number(
  import.meta.env.VITE_SANITY_CACHE_FALLBACK_TELEMETRY_SESSION_EVENT_LIMIT ?? 5,
);
const fallbackTelemetrySessionEventLimit =
  Number.isFinite(configuredFallbackTelemetrySessionEventLimit) && configuredFallbackTelemetrySessionEventLimit > 0
    ? configuredFallbackTelemetrySessionEventLimit
    : 5;
const draftAuthOptions: ClientConfig =
  perspective !== "published" && token
    ? {
        token,
        perspective,
        withCredentials: true,
      }
    : {};

type SanityFetchOptions = {
  queryName?: string;
  perspective?: ClientPerspective;
};

type SanityFetch = <TResult>(
  query: string,
  params?: Record<string, unknown>,
  options?: SanityFetchOptions,
) => Promise<TResult>;

const sharedSanityConfig: ClientConfig = {
  projectId: import.meta.env.VITE_SANITY_STUDIO_PROJECT_ID,
  dataset: import.meta.env.VITE_SANITY_STUDIO_DATASET,
  apiVersion: "2025-06-09", // use current date (YYYY-MM-DD) to target the latest API version
  perspective: "published",
  ...draftAuthOptions,
};

const cachedSanityClient: SanityClient = createClient({
  ...sharedSanityConfig,
  apiHost: cachedApiHost,
  useProjectHostname: cachedUseProjectHostname,
  useCdn: false,
});

const originSanityClient: SanityClient = createClient({
  ...sharedSanityConfig,
  apiHost: "https://api.sanity.io",
  useProjectHostname: true,
  useCdn: true,
});

const getStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const statusCode = Reflect.get(error, "statusCode");
  if (typeof statusCode === "number") {
    return statusCode;
  }

  const status = Reflect.get(error, "status");
  if (typeof status === "number") {
    return status;
  }

  const response = Reflect.get(error, "response");
  if (response && typeof response === "object") {
    const responseStatus = Reflect.get(response, "status");
    if (typeof responseStatus === "number") {
      return responseStatus;
    }
  }

  return undefined;
};

const classifySanityFallbackError = (
  error: unknown,
  statusCode?: number,
): "timeout" | "network" | "http_5xx" | "unknown" => {
  if (typeof statusCode === "number" && statusCode >= 500 && statusCode <= 599) {
    return "http_5xx";
  }

  if (error instanceof Error && error.message.includes("timed out after")) {
    return "timeout";
  }

  if (typeof statusCode === "undefined") {
    return "network";
  }

  return "unknown";
};

const reportedSanityFallbackQueryNames = new Set<string>();
const shouldReportSanityFallbackTelemetry = Math.random() < fallbackTelemetrySampleRate;

const reportSanityCacheFallback = ({
  queryName,
  error,
  statusCode,
  hadParams,
}: {
  queryName: string;
  error: unknown;
  statusCode?: number;
  hadParams: boolean;
}) => {
  if (!shouldReportSanityFallbackTelemetry) {
    return;
  }

  if (reportedSanityFallbackQueryNames.size >= fallbackTelemetrySessionEventLimit) {
    return;
  }

  if (reportedSanityFallbackQueryNames.has(queryName)) {
    return;
  }

  reportedSanityFallbackQueryNames.add(queryName);

  posthog.capture("sanity_cache_fallback", {
    query_name: queryName,
    status_code: statusCode ?? null,
    error_type: classifySanityFallbackError(error, statusCode),
    had_params: hadParams,
    sample_rate: fallbackTelemetrySampleRate,
  });
};

const withTimeout = async <TResult>(promise: Promise<TResult>, timeoutMs: number, label: string): Promise<TResult> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const fetchFromCachedSanity = async <TResult>(query: string, params?: Record<string, unknown>): Promise<TResult> => {
  if (typeof params === "undefined") {
    return withTimeout(cachedSanityClient.fetch<TResult>(query), cachedRequestTimeoutMs, "Cached Sanity request");
  }

  return withTimeout(cachedSanityClient.fetch<TResult>(query, params), cachedRequestTimeoutMs, "Cached Sanity request");
};

const fetchFromOriginSanity = async <TResult>(
  query: string,
  params?: Record<string, unknown>,
  options?: SanityFetchOptions,
): Promise<TResult> => {
  if (options?.perspective) {
    return originSanityClient.fetch<TResult>(query, params ?? {}, { perspective: options.perspective });
  }

  if (typeof params === "undefined") {
    return originSanityClient.fetch<TResult>(query);
  }

  return originSanityClient.fetch<TResult>(query, params);
};

const fetchWithFallback = async <TResult>(
  query: string,
  params?: Record<string, unknown>,
  options?: SanityFetchOptions,
): Promise<TResult> => {
  const queryName = options?.queryName ?? "unknown";
  const hadParams = typeof params !== "undefined";

  try {
    return await fetchFromCachedSanity<TResult>(query, params);
  } catch (error) {
    const statusCode = getStatusCode(error);
    const shouldFallback =
      (typeof statusCode === "number" && statusCode >= 500 && statusCode <= 599) || typeof statusCode === "undefined";

    if (!shouldFallback) {
      throw error;
    }

    reportSanityCacheFallback({ queryName, error, statusCode, hadParams });
    console.warn(`Cached Sanity endpoint failed${statusCode ? ` (${statusCode})` : ""}; falling back to origin.`);

    try {
      return await fetchFromOriginSanity<TResult>(query, params);
    } catch (originError) {
      const fallbackError = new Error(
        `Cached Sanity endpoint failed${statusCode ? ` (${statusCode})` : ""}; origin fallback also failed.`,
      );

      Sentry.captureException(fallbackError, {
        extra: {
          queryName,
          cacheError: error,
          originError,
        },
      });
      posthog.captureException(fallbackError);

      throw originError;
    }
  }
};

export const sanityClient: { fetch: SanityFetch } = {
  fetch: fetchWithFallback,
};

export const sanityOriginOnlyClient: { fetch: SanityFetch } = {
  fetch: fetchFromOriginSanity,
};

export type GetSongTypeDevotionalsQueryResult = Array<SongTypeDevotional>;

const devotionalAudioTrackProjection = `
  _key,
  _type,
	  audioFile {
	    contentType,
	    filename,
	    url,
	    fallbackUrl
	  },
	  "audioFilePath": coalesce(select(defined(audioFilePath) && audioFilePath != "" => audioFilePath), audioFile.url, null),
	  "fallbackAudioFilePath": coalesce(select(defined(fallbackAudioFilePath) && fallbackAudioFilePath != "" => fallbackAudioFilePath), audioFile.fallbackUrl, null),
	  "startDate": coalesce(startDate, null),
	  "expirationDate": coalesce(expirationDate, null),
	  "permissions": coalesce(permissions[], []),
	  title
	`;

const embeddedAudioDetailsProjection = `
	  "title": coalesce(title, null),
	  "audioFilePath": coalesce(select(defined(audioFilePath) && audioFilePath != "" => audioFilePath), audioFile.url, null),
	  "fallbackAudioFilePath": coalesce(select(defined(fallbackAudioFilePath) && fallbackAudioFilePath != "" => fallbackAudioFilePath), audioFile.fallbackUrl, null),
	  "audioContentMetadata": audioContentMetadata,
  "startDate": startDate,
  "expirationDate": expirationDate,
  "permissions": permissions[],
`;

// `composedAudio` is a rollout-only fallback for documents not yet migrated.
const devotionalEpisodeAudioProjection = `
  "compositeAudio": coalesce(compositeAudio, composedAudio, [])[] {
    _key,
    "_type": "episodeCompositeAudio",
    audio {
      title,
      audioFile {
        url,
        fallbackUrl,
        filename,
        contentType,
        size
      },
      audioFilePath,
      fallbackAudioFilePath,
      startDate,
      expirationDate,
      "permissions": coalesce(permissions[], [])
    },
    metadata {
      audiences,
      devotionalStartTimeSeconds,
      devotionalStopTimeSeconds,
      recipeVersion,
      variantKey
    }
  },
  "resolvedDevotionalAudio": devotionalTrack.devotionalAudio->{
    _id,
    _type,
    durationSeconds,
    lyricsBlock,
    audioChaptering {
      _type,
      sections[] {
        _key,
        _type,
        sectionType,
        title,
        startTimeSeconds,
        endTimeSeconds
      }
    },
    contributors[]-> { _id, name, slug { current } },
    songs[]-> {
      _id,
      title,
      slug { current }
    },
    audio[] {
${devotionalAudioTrackProjection}
    }
  },
  "devotionalTrack": select(
    (
      defined(devotionalTrack.audioDetails.audioFilePath) &&
      devotionalTrack.audioDetails.audioFilePath != ""
    ) ||
    defined(devotionalTrack.audioDetails.audioFile.url) ||
    defined(devotionalTrack.devotionalAudio->audio[
      (defined(audioFilePath) && audioFilePath != "") ||
      defined(audioFile.url)
    ][0]) => {
      "audioDetails": devotionalTrack.audioDetails {
${embeddedAudioDetailsProjection}
      },
      "devotionalAudio": devotionalTrack.devotionalAudio
    },
    defined(audio[
      (defined(audioFilePath) && audioFilePath != "") ||
      defined(audioFile.url)
    ][0]) => {
      "audioDetails": audio[
        (defined(audioFilePath) && audioFilePath != "") ||
        defined(audioFile.url)
      ][0] {
${embeddedAudioDetailsProjection}
      }
    }
  ),
`;

const referencedAdditionalAudioProjection = `
	  "title": coalesce(audio.title, title, null),
	  chapterType,
	  chapterSectionTitle,
	  "audioFilePath": coalesce(select(defined(audio.audioFilePath) && audio.audioFilePath != "" => audio.audioFilePath), audio.audioFile.url, null),
	  "fallbackAudioFilePath": coalesce(select(defined(audio.fallbackAudioFilePath) && audio.fallbackAudioFilePath != "" => audio.fallbackAudioFilePath), audio.audioFile.fallbackUrl, null),
	  "audioContentMetadata": audio.audioContentMetadata,
  "startDate": audio.startDate,
  "expirationDate": audio.expirationDate,
  "permissions": audio.permissions[],
`;

const introTrackProjection = `
  "introTrack": select(
    defined(introTrack[
      (
        defined(@->audio.audioFilePath) &&
        @->audio.audioFilePath != ""
      ) ||
      defined(@->audio.audioFile.url)
    ][0]._ref) => introTrack[
      (
        defined(@->audio.audioFilePath) &&
        @->audio.audioFilePath != ""
      ) ||
      defined(@->audio.audioFile.url)
    ]->{
${referencedAdditionalAudioProjection}
    },
    trailerAudio[
      (defined(audioFilePath) && audioFilePath != "") ||
      defined(audioFile.url)
    ]{
${embeddedAudioDetailsProjection}
    }
  ),
`;

const outroTrackProjection = `
  "outroTrack": select(
    defined(outroTrack[
      (
        defined(@->audio.audioFilePath) &&
        @->audio.audioFilePath != ""
      ) ||
      defined(@->audio.audioFile.url)
    ][0]._ref) => outroTrack[
      (
        defined(@->audio.audioFilePath) &&
        @->audio.audioFilePath != ""
      ) ||
      defined(@->audio.audioFile.url)
    ]->{
${referencedAdditionalAudioProjection}
    },
    outroAudio[
      (defined(audioFilePath) && audioFilePath != "") ||
      defined(audioFile.url)
    ]{
${embeddedAudioDetailsProjection}
    }
  ),
`;

const outroVideoVimeoIdProjection = `
  "outroVideoVimeoId": coalesce(outroVideoVimeoId, null),
`;

const meditationMomentProjection = `
  meditationMoment {
    audioDetails[] {
${devotionalAudioTrackProjection}
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      endChapter,
      endVerse,
      esvText,
    }
  },
`;

const managedExternalLinkProjection = `
  url,
  title,
  subtitle,
  startDate,
  expirationDate,
  "permissions": coalesce(permissions[], []),
  "imageUrl": images[0].imageUrl
`;

const externalResourceTabProjection = `
  "externalResourceTab": externalResourceTab[
    defined(@->url) && @->url != ""
  ]->{
${managedExternalLinkProjection}
  },
`;

const externalResourceLinksProjection = `
  "externalResourceLinks": externalResourceLinks[
    defined(@->url) && @->url != ""
  ]->{
${managedExternalLinkProjection}
  },
`;

// Optimized query for list view - only fetches essential fields limited to the latest 7 devotionals
export const getAllWorshipDevotionalsQuery = defineQuery(
  `*[
  _type == "worshipDevotional" && releaseDate <= now()] | order(releaseDate desc)[0..6] {
  _id,
  title,
  subtitle,
  contributors[]-> { _id, name, slug { current } },
  releaseDate,
  startShowingDate,
  slug {
    current,
  },
  enableAutoScroll,
  "thumbnail": images[imageType == "Thumbnail"][0].imageUrl,
  lyricsBlock,
  durationSeconds,
  ${meditationMomentProjection}
  images[] {
    imageType,
    imageName,
    imageUrl,
  },
${introTrackProjection}
${outroTrackProjection}
${outroVideoVimeoIdProjection}
${devotionalEpisodeAudioProjection}
  song -> {
    _id,
    title,
    slug { current }
  },
  ${externalResourceTabProjection}
  ${externalResourceLinksProjection}
  series[]->{
    _id,
    name,
    description,
    ownershipType,
    seriesType,
    freePeriod,
    shareBackdropImages[]{
      _key,
      _type,
      imageName,
      imageType,
      image,
      "imageUrl": image.asset->url
    },
    slug { current },
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      esvText,
    },
    contributors[]->{
      _id,
      name,
      biography,
      slug { current },
      images[] {
        imageType,
        imageName,
        imageUrl,
      },
    },
    ${externalResourceTabProjection}
    ${externalResourceLinksProjection}
  },
  _type
}`,
);

export const getSongTypeDevotionalsQuery = defineQuery(
  `*[
  _type == "worshipDevotional" &&
  count(series[
    _ref in *[_type == "sitePage" && pageType == "sing-archives"][0]
      .featuredContentCarousels[
        _type == "featuredContentCarousel" &&
        contentType == "devotionalSeries" &&
        isActive != false
      ].devotionalSeriesReferences[][
        @->isPrivate != true &&
        @->disabled != true &&
        @->publishDate <= now()
      ]._ref
  ]) > 0 &&
  defined(contentTags.songType) &&
  count(contentTags.songType) > 0 &&
  $songType in contentTags.songType[]->prefLabel &&
  releaseDate <= now()
] | order(releaseDate desc) {
  _id,
  title,
  subtitle,
  releaseDate,
  startShowingDate,
  seriesType,
  permissions[],
  sortOrder,
  scriptureReferences[] {
    book,
    startChapter,
    startVerse,
    endChapter,
    endVerse,
    esvText,
  },
  slug { current },
  series[]->{
    _id,
    name,
    seriesType,
    freePeriod,
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    ownershipType,
    slug { current },
  },
  _type
}`,
);

// Lightweight list of devotional series for the dashboard
export const getAllDevotionalSeriesForDashboard = defineQuery(
  `*[_type == "devotionalSeries" && disabled != true && publishDate <= now()] | order(publishDate desc, name asc) {
    _id,
    name,
    subtitle,
    ownershipType,
    isPrivate,
    "slug": slug.current,
    "description": coalesce(pt::text(description), null),
    seriesType,
    freePeriod,
    publishDate,
    "onboarding": {
      "organizationName": coalesce(onboarding.organizationName, null),
      "welcomeVideoVimeoId": coalesce(onboarding.welcomeVideoVimeoId, null),
      "welcomeTitle": coalesce(onboarding.welcomeTitle, null),
      "welcomeDescription": coalesce(onboarding.welcomeDescription, null)
    },
    "thumbnailUrl": coalesce(images[imageType == "Hero"][0].imageUrl, images[0].imageUrl, null),
    "featuredImageUrl": coalesce(images[imageType == "Feature"][0].imageUrl, null),
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
    "iconUrl": coalesce(images[imageType == "Icon"][0].imageUrl, null),
    "lockScreenUrl": coalesce(images[imageType == "Lock Screen"][0].imageUrl, null)
  }`,
);

// Detailed query for single devotional view
export const getWorshipDevotionalByDate = defineQuery(
  `*[_type == "worshipDevotional" && releaseDate == $releaseDate][0] {
  _id,
  title,
  subtitle,
  releaseDate,
  startShowingDate,
  permissions[],
  slug {
    current,
  },
  sharePercentage,
  enableAutoScroll,
  contributors[]-> { _id, name, slug { current } },
  lyricsBlock,
  durationSeconds,
  scriptureReferences[] {
    book,
    startChapter,
    startVerse,
    endChapter,
    endVerse,
    esvText,
  },
  images[] {
    imageType,
    imageName,
    imageUrl,
  },
${devotionalEpisodeAudioProjection}
${introTrackProjection}
${outroTrackProjection}
${outroVideoVimeoIdProjection}
  song -> {
    _id,
    title,
    slug {current}
    // add more attributes you need from the song document here
  },
  ${externalResourceTabProjection}
  ${externalResourceLinksProjection}
  series[]->{
    _id,
    name,
    description,
    ownershipType,
    seriesType,
    freePeriod,
    shareBackdropImages[]{
      _key,
      _type,
      imageName,
      imageType,
      image,
      "imageUrl": image.asset->url
    },
    slug { current },
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      esvText,
    },
    contributors[]->{
      _id,
      name,
      biography,
      slug { current },
      images[] {
        imageType,
        imageName,
        imageUrl,
      },
    },
    ${externalResourceTabProjection}
    ${externalResourceLinksProjection}
  },
  _type
}`,
);

// Query for episodes as parameter in array, if empty array it will return all episodes
export const getArrayDevotionalsByIdsQuery = defineQuery(
  `*[
  _type == "worshipDevotional" &&
  (
    (defined($ids) && count($ids) > 0 && _id in $ids) ||
    (!defined($ids) || count($ids) == 0 && releaseDate <= now())
  )
] | order(title asc) {
  _id,
  title,
  subtitle,
  contributors[]-> { _id, name, slug { current } },
  releaseDate,
  startShowingDate,
  permissions[],
  slug {
    current,
  },
  sharePercentage,
  enableAutoScroll,
  lyricsBlock,
  durationSeconds,
  scriptureReferences[] {
    book,
    startChapter,
    startVerse,
    endChapter,
    endVerse,
    esvText,
  },
  images[] {
    imageType,
    imageName,
    imageUrl,
  },
${devotionalEpisodeAudioProjection}
${introTrackProjection}
${outroTrackProjection}
${outroVideoVimeoIdProjection}
  song -> {
    _id,
    title,
    slug {current}
    // add more attributes you need from the song document here
  },
  ${externalResourceTabProjection}
  ${externalResourceLinksProjection}
  series[]->{
    _id,
    name,
    description,
    ownershipType,
    seriesType,
    freePeriod,
    shareBackdropImages[]{
      _key,
      _type,
      imageName,
      imageType,
      image,
      "imageUrl": image.asset->url
    },
    slug { current },
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      esvText,
    },
    contributors[]->{
      _id,
      name,
      biography,
      slug { current },
      images[] {
        imageType,
        imageName,
        imageUrl,
      },
    },
    ${externalResourceTabProjection}
    ${externalResourceLinksProjection}
  },
  _type
}`,
);

// Query for all worship devotional minimum data, specially for search and listing
// This query fetches only the essential fields to minimize data transfer and improve performance
export const getAllWorshipDevotionalsMinimalQuery = defineQuery(
  `*[_type == "worshipDevotional" &&
  releaseDate <= now()
] | order(releaseDate desc) {
  _id,
  title,
  subtitle,
  releaseDate,
  startShowingDate,
  _type
}`,
);

export const getAllWorshipDevotionalsForBibleBrowseQuery = defineQuery(
  `*[
    _type == "worshipDevotional" &&
    releaseDate <= now() &&
    count(series[
      _ref in *[_type == "sitePage" && pageType == "sing-archives"][0]
        .featuredContentCarousels[
          _type == "featuredContentCarousel" &&
          contentType == "devotionalSeries" &&
          isActive != false
        ].devotionalSeriesReferences[][
          @->isPrivate != true &&
          @->disabled != true &&
          @->publishDate <= now()
        ]._ref
    ]) > 0
  ] | order(releaseDate desc) {
    _id,
    scriptureReferences[] {
      book,
    }
  }`,
);

// Query to fetch a single devotional by its ID
// This query retrieves all necessary fields for a detailed view of a devotional
export const getSingleDevotionalById = defineQuery(
  `*[_type == "worshipDevotional" && _id == $id][0] {
  _id,
  title,
  subtitle,
  contributors[]-> { _id, name, slug { current } },
  releaseDate,
  startShowingDate,
  permissions[],
  slug {
    current,
  },
  sharePercentage,
  enableAutoScroll,
  lyricsBlock,
  durationSeconds,
  scriptureReferences[] {
    book,
    startChapter,
    startVerse,
    endChapter,
    endVerse,
    esvText,
  },
  images[] {
    imageType,
    imageName,
    imageUrl,
  },
${devotionalEpisodeAudioProjection}
${introTrackProjection}
${outroTrackProjection}
${outroVideoVimeoIdProjection}
  song -> {
    _id,
    title,
    slug {current}
    // add more attributes you need from the song document here
  },
  ${externalResourceTabProjection}
  ${externalResourceLinksProjection}
  series[]->{
    _id,
    name,
    description,
    ownershipType,
    seriesType,
    freePeriod,
    shareBackdropImages[]{
      _key,
      _type,
      imageName,
      imageType,
      image,
      "imageUrl": image.asset->url
    },
    slug { current },
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      esvText,
    },
    contributors[]->{
      _id,
      name,
      biography,
      slug { current },
      images[] {
        imageType,
        imageName,
        imageUrl,
      },
    },
    ${externalResourceTabProjection}
    ${externalResourceLinksProjection}
  },
  _type
}`,
);

export const getDevotionalsByBibleBookQuery = defineQuery(
  `*[
    _type == "worshipDevotional" &&
    releaseDate <= now() &&
    count(series[
      _ref in *[_type == "sitePage" && pageType == "sing-archives"][0]
        .featuredContentCarousels[
          _type == "featuredContentCarousel" &&
          contentType == "devotionalSeries" &&
          isActive != false
        ].devotionalSeriesReferences[][
          @->isPrivate != true &&
          @->disabled != true &&
          @->publishDate <= now()
        ]._ref
    ]) > 0 &&
    count(scriptureReferences[book == $book]) > 0
  ] | order(releaseDate desc, title asc) {
    _id,
    title,
    subtitle,
    releaseDate,
    startShowingDate,
    permissions[],
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      endChapter,
      endVerse,
      esvText,
    },
    slug { current },
    series[]->{
      _id,
      name,
      seriesType,
      freePeriod,
      ownershipType,
      freePeriod,
      slug { current },
      "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
      images[] {
        imageType,
        imageName,
        imageUrl,
      },
    },
    _type
  }`,
);

// Query to fetch devotionals by array of IDs and limitDate (7 days before today) for Devo Free Users
export const getFavoritesForFreeUsersQuery = defineQuery(
  `*[
  _type == "worshipDevotional" &&
  _id in $ids &&
  releaseDate >= $limitDate
] | order(title asc) {
  _id,
  title,
  subtitle,
  releaseDate,
  startShowingDate,
  permissions[],
  contributors[]-> { _id, name, slug { current } },
  sharePercentage,
  enableAutoScroll,
  lyricsBlock,
  durationSeconds,
  ${meditationMomentProjection}
  scriptureReferences[] {
    book,
    startChapter,
    startVerse,
    endChapter,
    endVerse,
    esvText,
  },
  slug { current },
  images[] {
    imageType,
    imageName,
    imageUrl,
  },
${devotionalEpisodeAudioProjection}
${introTrackProjection}
${outroTrackProjection}
${outroVideoVimeoIdProjection}
  song -> {
    _id,
    title,
    slug { current }
  },
  ${externalResourceTabProjection}
  ${externalResourceLinksProjection}
  series[]->{
    _id,
    name,
    description,
    ownershipType,
    seriesType,
    freePeriod,
    shareBackdropImages[]{
      _key,
      _type,
      imageName,
      imageType,
      image,
      "imageUrl": image.asset->url
    },
    slug { current },
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      esvText,
    },
    contributors[]->{
      _id,
      name,
      biography,
      slug { current },
      images[] {
        imageType,
        imageName,
        imageUrl,
      },
    },
    ${externalResourceTabProjection}
    ${externalResourceLinksProjection}
  },
  _type
}`,
);

// Treat missing ownershipType as internal, then compare it to the requested ownership.
export const getSeriesBySlugQuery = defineQuery(
  `*[
  _type == "devotionalSeries" &&
  slug.current == $seriesSlug &&
  coalesce(ownershipType, "internal") == $ownershipType
][0] {
  _id,
  name,
  subtitle,
  ownershipType,
  isPrivate,
  description,
  seriesType,
  freePeriod,
  publishDate,
  "onboarding": {
    "organizationName": coalesce(onboarding.organizationName, null),
    "welcomeVideoVimeoId": coalesce(onboarding.welcomeVideoVimeoId, null),
    "welcomeTitle": coalesce(onboarding.welcomeTitle, null),
    "welcomeDescription": coalesce(onboarding.welcomeDescription, null)
  },
  shareBackdropImages[]{
    _key,
    _type,
    imageName,
    imageType,
    image,
    "imageUrl": image.asset->url
  },
  slug { current },
  contributors[]-> { _id, name, slug { current } },
  "featureUrl": coalesce(images[imageType == "Feature"][0].imageUrl, images[0].imageUrl, null),
  "thumbnailUrl": coalesce(images[imageType == "Thumbnail"][0].imageUrl, images[0].imageUrl, null),
  "heroUrl": coalesce(images[imageType == "Hero"][0].imageUrl, images[0].imageUrl, null),
  "backgroundUrl": coalesce(images[imageType == "Background"][0].imageUrl, images[0].imageUrl, null),
  "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
  "iconUrl": coalesce(images[imageType == "Icon"][0].imageUrl, null),
  "lockScreenUrl": coalesce(images[imageType == "Lock Screen"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
  ${externalResourceTabProjection}
  ${externalResourceLinksProjection}
}`,
);

const episodeBySlugsFilter = `*[
  _type == "worshipDevotional" &&
  slug.current == $episodeSlug
][
  count(series[
    _ref in *[
      _type == "devotionalSeries" &&
      slug.current == $seriesSlug &&
      coalesce(ownershipType, "internal") == $ownershipType
    ]._id
  ]) > 0
][0]`;

const episodeShellProjection = `
  title,
  subtitle,
  contributors[]-> { _id, name, slug { current } },
  releaseDate,
  startShowingDate,
  permissions[],
  slug {
    current,
  },
  sharePercentage,
  enableAutoScroll,
  lyricsBlock,
  durationSeconds,
  scriptureReferences[] {
    book,
    startChapter,
    startVerse,
    endChapter,
    endVerse,
    esvText,
  },
  images[] {
    imageType,
    imageName,
    imageUrl,
  },
${introTrackProjection}
${outroTrackProjection}
${outroVideoVimeoIdProjection}
  song -> {
    _id,
    title,
    slug {current}
    // add more attributes you need from the song document here
  },
  ${externalResourceTabProjection}
  ${externalResourceLinksProjection}
`;

const episodeMediaProjection = `
  ${meditationMomentProjection}
  ${devotionalEpisodeAudioProjection}
`;

const episodeSeriesContextProjection = `
  series[]->{
    _id,
    name,
    description,
    ownershipType,
    seriesType,
    freePeriod,
    previewKey,
    slug { current },
    shareBackdropImages[]{
      _key,
      _type,
      imageName,
      imageType,
      image,
      "imageUrl": image.asset->url
    },
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      esvText,
    },
    contributors[]->{
      _id,
      name,
      biography,
      slug { current },
      images[] {
        imageType,
        imageName,
        imageUrl,
      },
    },
    ${externalResourceTabProjection}
    ${externalResourceLinksProjection}
  },
`;

export const getEpisodeBySlugAndSeriesSlugQuery = defineQuery(
  `${episodeBySlugsFilter}{
  _id,
  ${episodeShellProjection}
  ${episodeMediaProjection}
  ${episodeSeriesContextProjection}
}`,
);

export const getEpisodeShellBySlugAndSeriesSlugQuery = defineQuery(
  `${episodeBySlugsFilter}{
  _id,
  _rev,
  ${episodeShellProjection}
}`,
);

export const getEpisodeMediaBySlugAndSeriesSlugQuery = defineQuery(
  `${episodeBySlugsFilter}{
  _id,
  _rev,
  ${episodeMediaProjection}
}`,
);

export const getEpisodeSeriesContextBySlugAndSeriesSlugQuery = defineQuery(
  `${episodeBySlugsFilter}{
  _id,
  _rev,
  ${episodeSeriesContextProjection}
}`,
);

export type EpisodeBySlugsParams = {
  seriesSlug: string;
  episodeSlug: string;
  ownershipType: string;
};

type EpisodeSliceClient = { fetch: SanityFetch };

// The atomic and sliced queries share projection fragments; derive the slice
// contracts from the established result so their merge stays type-complete.
type EpisodeResult = NonNullable<GetEpisodeBySlugAndSeriesSlugQueryResult>;
type EpisodeShellSliceResult =
  | (Omit<EpisodeResult, "meditationMoment" | "resolvedDevotionalAudio" | "devotionalTrack" | "series"> & {
      _rev: string;
    })
  | null;
type EpisodeMediaSliceResult =
  | (Pick<
      EpisodeResult,
      "_id" | "compositeAudio" | "meditationMoment" | "resolvedDevotionalAudio" | "devotionalTrack"
    > & {
      _rev: string;
    })
  | null;
type EpisodeSeriesContextSliceResult =
  | (Pick<EpisodeResult, "_id" | "series"> & {
      _rev: string;
    })
  | null;

type EpisodeSliceInconsistencyReason = "partial-result" | "id-mismatch" | "revision-mismatch";

const episodeQueryNames = {
  aggregate: "episode_by_slugs",
  shell: "episode_shell_by_slugs",
  media: "episode_media_by_slugs",
  seriesContext: "episode_series_context_by_slugs",
} as const;

export class InconsistentEpisodeSlicesError extends Error {
  readonly reason: EpisodeSliceInconsistencyReason;

  constructor(reason: EpisodeSliceInconsistencyReason) {
    super(`Episode query slices were inconsistent: ${reason}`);
    this.name = "InconsistentEpisodeSlicesError";
    this.reason = reason;
  }
}

export const fetchEpisodeBySlugsInSlices = async (
  client: EpisodeSliceClient,
  params: EpisodeBySlugsParams,
): Promise<GetEpisodeBySlugAndSeriesSlugQueryResult> => {
  const [shell, media, seriesContext] = await Promise.all([
    client.fetch<EpisodeShellSliceResult>(getEpisodeShellBySlugAndSeriesSlugQuery, params, {
      queryName: episodeQueryNames.shell,
    }),
    client.fetch<EpisodeMediaSliceResult>(getEpisodeMediaBySlugAndSeriesSlugQuery, params, {
      queryName: episodeQueryNames.media,
    }),
    client.fetch<EpisodeSeriesContextSliceResult>(getEpisodeSeriesContextBySlugAndSeriesSlugQuery, params, {
      queryName: episodeQueryNames.seriesContext,
    }),
  ]);

  if (shell === null && media === null && seriesContext === null) {
    return null;
  }

  if (shell === null || media === null || seriesContext === null) {
    throw new InconsistentEpisodeSlicesError("partial-result");
  }

  if (new Set([shell._id, media._id, seriesContext._id]).size !== 1) {
    throw new InconsistentEpisodeSlicesError("id-mismatch");
  }

  const { _rev: shellRevision, ...shellWithoutRevision } = shell;
  if (new Set([shellRevision, media._rev, seriesContext._rev]).size !== 1) {
    throw new InconsistentEpisodeSlicesError("revision-mismatch");
  }

  return {
    ...shellWithoutRevision,
    compositeAudio: media.compositeAudio,
    meditationMoment: media.meditationMoment,
    resolvedDevotionalAudio: media.resolvedDevotionalAudio,
    devotionalTrack: media.devotionalTrack,
    series: seriesContext.series,
  } satisfies NonNullable<GetEpisodeBySlugAndSeriesSlugQueryResult>;
};

export const fetchEpisodeBySlugsFromCachedSanity = async (
  params: EpisodeBySlugsParams,
): Promise<GetEpisodeBySlugAndSeriesSlugQueryResult> => {
  try {
    return await fetchEpisodeBySlugsInSlices(sanityClient, params);
  } catch (error) {
    if (!(error instanceof InconsistentEpisodeSlicesError)) {
      throw error;
    }

    posthog.capture("sanity_episode_cache_consistency_fallback", {
      query_name: episodeQueryNames.aggregate,
      reason: error.reason,
    });
    console.warn("Cached episode query slices were inconsistent; using the atomic Sanity fallback.");

    return sanityOriginOnlyClient.fetch<GetEpisodeBySlugAndSeriesSlugQueryResult>(
      getEpisodeBySlugAndSeriesSlugQuery,
      params,
    );
  }
};

// Query to get the latest available episode from a series
// Returns only _id, title, subtitle, and scriptureReferences
// Note: Fetches multiple episodes to allow client-side timezone filtering
export const getLatestAvailableEpisodeFromSerie = defineQuery(
  `*[
    _type == "worshipDevotional" &&
    releaseDate <= $today &&
    references($seriesId)
  ] | order(releaseDate desc)[0] {
    _id,
    "name": title,
    subtitle,
    releaseDate,
    permissions[],
    slug {
      current,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      endChapter,
      endVerse,
      esvText,
    }
  }`,
);

// Query to get the first episode from a series (oldest release date)
// Returns only _id, name, subtitle, and scriptureReferences
export const getFirstEpisodeFromSeries = defineQuery(
  `*[
    _type == "worshipDevotional" &&
    releaseDate <= now() &&
    references($seriesId)
  ] | order(releaseDate asc)[0] {
    _id,
    "name": title,
    subtitle,
    releaseDate,
    permissions[],
    slug {
      current,
    },
    scriptureReferences[] {
      book,
      startChapter,
      startVerse,
      endChapter,
      endVerse,
      esvText,
    }
  }`,
);

export const getAllEpisodesBySeriesSlugQuery = defineQuery(
  `*[
    _type == "worshipDevotional" &&
    count(series[@->slug.current == $seriesSlug && coalesce(@->ownershipType, "internal") == $ownershipType]) > 0 &&
    (
      (startShowingDate <= $today && releaseDate <= $today) ||
      (
        defined($previewKey) && $previewKey != "" &&
        count(series[
          @->slug.current == $seriesSlug &&
          coalesce(@->ownershipType, "internal") == $ownershipType &&
          @->previewKey == $previewKey
        ]) > 0
      )
    )
  ] | order(releaseDate asc, sortOrder asc) {
  _id,
  title,
  subtitle,
  releaseDate,
  startShowingDate,
  seriesType,
  permissions[],
  sortOrder,
  scriptureReferences[] {
    book,
    startChapter,
    startVerse,
    endChapter,
    endVerse,
    esvText,
  },
  slug { current },
  series[]->{
    _id,
    name,
    "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
${introTrackProjection}
${outroTrackProjection}
    images[] {
      imageType,
      imageName,
      imageUrl,
    },
    ownershipType,
    slug { current },
  },
  _type
}`,
);

export const getAllEpisodesMinimalBySeriesSlugQuery = defineQuery(
  `*[
    _type == "worshipDevotional" &&
    count(series[@->slug.current == $seriesSlug && coalesce(@->ownershipType, "internal") == $ownershipType]) > 0 &&
    startShowingDate <= $today
  ] | order(releaseDate asc, sortOrder asc) {
    _id,
    title,
    subtitle,
    releaseDate,
    permissions[],
    slug { current },
    scriptureReferences,
    _type
  }`,
);

// Query to fetch the sing-home site page with featured content carousels
export const getSingHomePageQuery = defineQuery(
  `*[_type == "sitePage" && pageType == "sing-home"][0] {
    _id,
    name,
    description,
    featuredContentCarousels[_type == "featuredContentCarousel"] {
      carouselName,
      contentType,
      isActive,
      devotionalSeriesReferences[]->{
        _id,
        name,
        subtitle,
        ownershipType,
        "slug": slug.current,
        "description": coalesce(pt::text(description), null),
        seriesType,
        publishDate,
        isPrivate,
        "thumbnailUrl": coalesce(images[imageType == "Hero"][0].imageUrl, images[0].imageUrl, null),
        "featuredImageUrl": coalesce(images[imageType == "Feature"][0].imageUrl, null),
        "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
        "iconUrl": coalesce(images[imageType == "Icon"][0].imageUrl, null),
        "lockScreenUrl": coalesce(images[imageType == "Lock Screen"][0].imageUrl, null)
      }
    }
  }`,
);

// Query to fetch the sing-archives site page with featured content carousels
export const getSingArchivesPageQuery = defineQuery(
  `*[_type == "sitePage" && pageType == "sing-archives"][0] {
    _id,
    name,
    description,
    featuredContentCarousels[_type == "featuredContentCarousel"] {
      carouselName,
      contentType,
      isActive,
      devotionalSeriesReferences[]->{
        _id,
        name,
        subtitle,
        ownershipType,
        "slug": slug.current,
        "description": coalesce(pt::text(description), null),
        seriesType,
        publishDate,
        isPrivate,
        "thumbnailUrl": coalesce(images[imageType == "Hero"][0].imageUrl, images[0].imageUrl, null),
        "featuredImageUrl": coalesce(images[imageType == "Feature"][0].imageUrl, null),
        "logomarkUrl": coalesce(images[imageType == "Logomark"][0].imageUrl, null),
        "iconUrl": coalesce(images[imageType == "Icon"][0].imageUrl, null),
        "lockScreenUrl": coalesce(images[imageType == "Lock Screen"][0].imageUrl, null)
      }
    }
  }`,
);
